import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetVenuaPlannerTermTransportScheme,
  useSaveVenuaPlannerTermTransportScheme,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { LayerState, type CalendarTermTransport } from '@/api/generated/schemas';
import { useSetTermTransportSchemeOwnership } from '../../../serie/internal/transportScheme';
import { StationSelector } from '@/modules/products/components/StationSelector';
import { StationChainSelector } from '@/modules/products/components/StationChainSelector';
import type { StationChainRef, StationRef } from '@/modules/products/productsApi';
import { Button, Field, Switch, TimeBox, hhmmToMinutes, minutesToHhmm, toast } from '@/shared/ui';
import { StationLink } from '../../../components/StationLink';

interface TermTransportSchemeEditorProps {
  productId: string;
  serieId: string;
  termId: string;
  termTransport: CalendarTermTransport;
  /** Stav dopravní vrstvy termínu – řídí editovatelnost (Approved = read-only). */
  operationLayerState: LayerState;
  /** Schvalovací tlačítko vrstvy – dodává nadřazený panel, aby bylo jednotné se všemi vrstvami. */
  approveAction?: React.ReactNode;
  /** Po uložení dopravy – invalidace kalendáře a stromu řeší volající. */
  onSaved: () => void;
}

function toStationRef(
  id: number | null | undefined,
  name: string | null | undefined
): StationRef | null {
  if (id == null) return null;
  return {
    stationId: id,
    name: name ?? '',
    code: null,
    isDomestic: false,
    latitude: null,
    longitude: null,
  };
}

function toChainRef(
  id: string | null | undefined,
  name: string | null | undefined
): StationChainRef | null {
  if (id == null) return null;
  return { stationChainId: id, name: name ?? '', code: null, isReturn: false };
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-phx-xs text-phx-text-2 mb-1 font-semibold tracking-wide uppercase">
      {children}
    </h4>
  );
}

/**
 * Editor autobusové dopravy termínu v sidebaru kalendáře. Dvě sekce – „Main Transport Scheme" (přepínač
 * dědění + stanice/čas pro směr tam a zpět) a „Přiřazené trasy" (linka + Reverse pro směr tam a zpět) – jsou
 * kvůli úzkému panelu poskládané kompaktně: každý směr = jeden řádek (vlevo selector, vpravo menší čas / switch),
 * label vždy nad inputem. Po schválení dopravní vrstvy (`operationLayerState = Approved`) je editor jen pro čtení.
 *
 * Vlastní rám (Panel, titulek, stavový badge) obstarává nadřazený `TermLayerPanel` – tady zůstává jen obsah,
 * aby doprava vypadala stejně jako ostatní vrstvy v sidebaru a nevznikl panel v panelu.
 */
export function TermTransportSchemeEditor({
  productId,
  serieId,
  termId,
  termTransport,
  operationLayerState,
  approveAction,
  onSaved,
}: TermTransportSchemeEditorProps) {
  const { t } = useTranslation('venua-planner');
  const saveMutation = useSaveVenuaPlannerTermTransportScheme();

  // Uložení jde přes společný kontrakt záložky Doprava, takže se schéma nejdřív načte a šuplík do něj
  // promítne jen to, co edituje — prázdný seznam ticketů by jinak smazal celou definici.
  const schemeQuery = useGetVenuaPlannerTermTransportScheme(productId, serieId, termId);
  const setOwnScheme = useSetTermTransportSchemeOwnership({
    productId,
    serieId,
    termId,
    onDone: onSaved,
  });

  const readOnly = operationLayerState === LayerState.Approved;

  const [overridden, setOverridden] = useState(termTransport.isOwnScheme);
  const [arrivalStation, setArrivalStation] = useState<StationRef | null>(
    toStationRef(termTransport.outbound?.stationId, termTransport.outbound?.stationName)
  );
  const [arrivalTime, setArrivalTime] = useState<number | null>(
    termTransport.outbound?.timeMinutes ?? null
  );
  const [departureStation, setDepartureStation] = useState<StationRef | null>(
    toStationRef(termTransport.inbound?.stationId, termTransport.inbound?.stationName)
  );
  const [departureTime, setDepartureTime] = useState<number | null>(
    termTransport.inbound?.timeMinutes ?? null
  );
  const [outboundChain, setOutboundChain] = useState<StationChainRef | null>(
    toChainRef(termTransport.outbound?.stationChainId, termTransport.outbound?.stationChainName)
  );
  const [outboundReversed, setOutboundReversed] = useState(
    termTransport.outbound?.stationChainReversed ?? false
  );
  const [inboundChain, setInboundChain] = useState<StationChainRef | null>(
    toChainRef(termTransport.inbound?.stationChainId, termTransport.inbound?.stationChainName)
  );
  const [inboundReversed, setInboundReversed] = useState(
    termTransport.inbound?.stationChainReversed ?? false
  );

  const unset = t('calendar.drawer.bus.unset');
  // Stanice a časy patří definici — editovat je smí jen termín, který ji vlastní.
  const stationsEditable = overridden && !readOnly;

  const doSave = async (): Promise<boolean> => {
    try {
      const scheme = schemeQuery.data;
      if (!scheme) return false;

      await saveMutation.mutateAsync({
        productId,
        serieId,
        termId,
        data: {
          kind: scheme.kind,
          costCurrency: scheme.costCurrency,
          // Kapacita seriálu se ze šuplíku termínu nemění — mění se v matici na seriálu.
          sharedCapacity: null,
          directions: [
            {
              direction: 'Outbound',
              stationId: arrivalStation?.stationId ?? null,
              timeMinutes: arrivalTime,
            },
            {
              direction: 'Inbound',
              stationId: departureStation?.stationId ?? null,
              timeMinutes: departureTime,
            },
          ],
          tickets: scheme.tickets.map((ticket) => ({
            ticketId: ticket.ticketId,
            kind: ticket.kind,
            regularTransportId: ticket.regularTransportId,
            outboundSequence: ticket.outboundSequence,
            inboundSequence: ticket.inboundSequence,
            expectedPaxAmount: ticket.expectedPaxAmount,
            expectedGroupAmount: ticket.expectedGroupAmount,
          })),
          terms: [
            {
              termId,
              isOwnCapacity: scheme.rows[0]?.isOwnCapacity ?? false,
              capacity: scheme.rows[0]?.capacity ?? null,
              costs: (scheme.rows[0]?.costs ?? []).map((cost) => ({
                ticketId: cost.ticketId,
                isManual: cost.isManual,
                paxAmount: cost.paxAmount,
                groupAmount: cost.groupAmount,
              })),
              directions: [
                {
                  direction: 'Outbound',
                  stationChainId: outboundChain?.stationChainId ?? null,
                  stationChainReversed: outboundReversed,
                },
                {
                  direction: 'Inbound',
                  stationChainId: inboundChain?.stationChainId ?? null,
                  stationChainReversed: inboundReversed,
                },
              ],
            },
          ],
        },
      });
      return true;
    } catch {
      toast({
        severity: 'error',
        title: t('calendar.drawer.bus.errorTitle'),
        description: t('calendar.drawer.bus.saveError'),
      });
      return false;
    }
  };

  const handleSave = async () => {
    if (!(await doSave())) return;
    onSaved();
    toast({ severity: 'success', description: t('calendar.drawer.bus.saved') });
  };

  /** Jeden řádek „stanice + čas": vlevo selector (nebo read-only stanice), vpravo menší výběr času. */
  const stationRow = (
    stationLabel: string,
    timeLabel: string,
    station: StationRef | null,
    onStation: (next: StationRef | null) => void,
    time: number | null,
    onTime: (next: number | null) => void
  ) => (
    <div className="gap-phx-2 flex items-end">
      <div className="min-w-0 flex-1">
        {stationsEditable ? (
          <StationSelector
            mode="single"
            label={stationLabel}
            placeholder={t('calendar.drawer.bus.stationPlaceholder')}
            extraFilters={{ domestic: false }}
            selected={station ? [station] : []}
            onChange={(next) => onStation(next[0] ?? null)}
          />
        ) : (
          <Field label={stationLabel}>
            {station ? (
              <StationLink stationId={station.stationId}>
                <span className="text-phx-text">{station.name}</span>
              </StationLink>
            ) : (
              <span className="text-phx-text-muted">{unset}</span>
            )}
          </Field>
        )}
      </div>
      <div className="w-32 shrink-0">
        {stationsEditable ? (
          <TimeBox
            label={timeLabel}
            value={minutesToHhmm(time)}
            onChange={(next) => onTime(hhmmToMinutes(next))}
          />
        ) : (
          <Field label={timeLabel}>
            <span className="text-phx-text tabular-nums">{minutesToHhmm(time) || unset}</span>
          </Field>
        )}
      </div>
    </div>
  );

  /** Jeden řádek „linka + Reverse": vlevo selector (nebo read-only název linky), vpravo switch Reverse. */
  const routeRow = (
    chainLabel: string,
    chain: StationChainRef | null,
    onChain: (next: StationChainRef | null) => void,
    reversed: boolean,
    onReversed: (next: boolean) => void
  ) => (
    <div className="gap-phx-3 flex items-end">
      <div className="min-w-0 flex-1">
        {readOnly ? (
          <Field label={chainLabel}>
            <span className={chain ? 'text-phx-text' : 'text-phx-text-muted'}>
              {chain?.name ?? unset}
            </span>
          </Field>
        ) : (
          <StationChainSelector
            mode="single"
            label={chainLabel}
            selected={chain ? [chain] : []}
            onChange={(next) => onChain(next[0] ?? null)}
          />
        )}
      </div>
      <div className="shrink-0 pb-1.5">
        <Switch
          checked={reversed}
          onCheckedChange={onReversed}
          disabled={readOnly}
          label={t('calendar.drawer.bus.reversed')}
        />
      </div>
    </div>
  );

  return (
    <div className="gap-phx-4 flex flex-col">
      {/* Sekce 1 – Main Transport Scheme (přepínač dědění + stanice/čas tam a zpět) */}
      <section className="gap-phx-3 flex flex-col">
        <SectionHeader>{t('calendar.drawer.bus.mainSchemeTitle')}</SectionHeader>

        {/* Přepnutí Sdíleno ↔ Vlastní mění efektivní definici, takže jde rovnou na server. */}
        <Switch
          checked={!overridden}
          onCheckedChange={(checked) => {
            setOverridden(!checked);
            setOwnScheme(!checked);
          }}
          disabled={readOnly}
          label={t('calendar.drawer.bus.inheritFromSeries')}
        />

        <div className="gap-phx-3 flex flex-col">
          {stationRow(
            t('calendar.drawer.bus.arrivalStation'),
            t('calendar.drawer.bus.arrivalTime'),
            arrivalStation,
            setArrivalStation,
            arrivalTime,
            setArrivalTime
          )}
          {stationRow(
            t('calendar.drawer.bus.departureStation'),
            t('calendar.drawer.bus.departureTime'),
            departureStation,
            setDepartureStation,
            departureTime,
            setDepartureTime
          )}
        </div>
      </section>

      {/* Dělící čára mezi sub-sekcemi panelu. */}
      <div className="border-phx-border border-t" />

      {/* Sekce 2 – Přiřazené trasy (linka + Reverse tam a zpět) */}
      <section className="gap-phx-3 flex flex-col">
        <SectionHeader>{t('calendar.drawer.bus.routesTitle')}</SectionHeader>

        <div className="gap-phx-3 flex flex-col">
          {routeRow(
            t('calendar.drawer.bus.outboundChain'),
            outboundChain,
            setOutboundChain,
            outboundReversed,
            setOutboundReversed
          )}
          {routeRow(
            t('calendar.drawer.bus.inboundChain'),
            inboundChain,
            setInboundChain,
            inboundReversed,
            setInboundReversed
          )}
        </div>
      </section>

      {readOnly ? (
        <p className="text-phx-text-muted text-phx-xs italic">
          {t('calendar.drawer.bus.approvedHint')}
        </p>
      ) : (
        <div className="gap-phx-2 flex items-center justify-end">
          {approveAction}
          <Button
            type="button"
            variant="primary"
            iconLeft="check"
            onClick={() => void handleSave()}
            disabled={saveMutation.isPending || schemeQuery.isLoading}
            loading={saveMutation.isPending}
          >
            {t('calendar.drawer.bus.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
